import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'frontend-login/config/environment';
import fetch from 'frontend-login/utils/fetch';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';

export default class PasswordResetController extends Controller {
  queryParams = ['email', 'success'];

  @service declare firebaseService: Services['firebase-service'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked email = '';
  @tracked hasServerError = false;
  @tracked isEmailValid = true;
  @tracked success = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pageTitle() {
    return this.intl.t('forgot_your_password.reset');
  }

  validateEmail(): void {
    this.isEmailValid = isValidEmailAddress(this.email);
  }

  @action
  goBack(): void {
    this.success = false;
    this.router.transitionTo('login');
  }

  @action
  async resetPassword(): Promise<void> {
    this.hasServerError = false;
    this.validateEmail();
    if (!this.isEmailValid) {
      return;
    }

    try {
      const host = getHostDomain({ frontendGateway: ENV.frontendGateway });
      await fetch(`${host}/apigateway/v1/app/password-reset-request`, {
        body: JSON.stringify({
          email: this.email,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      // Clear firebase auth data
      await this.firebaseService.auth.signOut();
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await indexedDB.deleteDatabase('firebaseLocalStorageDb');

      // Construct URL with query params
      const url = this.router.urlFor('password-reset', {
        queryParams: {
          success: true,
          email: this.email,
        },
      });

      window.location.href = url;
    } catch {
      this.hasServerError = true;
    }
  }

  @action
  setEmail(event: Event): void {
    this.email = (event.target as HTMLInputElement).value;
    this.hasServerError = false;
    this.validateEmail();
  }

  @action
  submitFormWithEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.resetPassword();
    }
  }
}
